import React from 'react';
import clsx, { ClassValue } from 'clsx';
import { Typography } from '../../common/typography/typography';
import { Icon } from '@/components/common';
import Icons from '@/const/icons';
import { Combobox, Options } from '@/components/ui/combobox';
import { useSearchParams } from 'next/navigation';

export interface FiltersStripe {
	[key: string]: Options[] | JSX.Element;
}
interface Props {
	filters: FiltersStripe;
	className?: ClassValue;
	handleSelectFilters: (key: string, value: string | undefined) => void;
	// This values align headers to the end
	headersAlignEnd?: number[];
}

export const FiltersStripe: React.FC<Props> = ({
	filters,
	className,
	handleSelectFilters,
	headersAlignEnd,
}) => {
	const searchParams = useSearchParams();
	const params = new URLSearchParams(searchParams);

	const [indexOpen, setIndexOpen] = React.useState<number[]>([]);

	const handleOpen = (index: number) => {
		const indexValues = indexOpen.includes(index)
			? indexOpen.filter((value) => value !== index)
			: [...indexOpen, index];
		setIndexOpen(indexValues);
	};
	const widthMap: Record<string, string> = {
		Clients: '!w-[343px] pb-4 !max-h-fit',
		Subject: '!w-[274px] !h-[450px] pb-4',
		Cases: '!w-[354px] pb-4',
		Date: '!w-[354px] pb-4',
	};

	const titleMap: Record<string, string> = {
		Clients: 'Client',
		Cases: 'Case',
		Subject: 'Subject', // unchanged, but included for completeness
		Date: 'Date',
	};
	const commandListClassNameMap: Record<string, string> = {
		Clients: '!max-h-[384px] overflow-y-auto',
		Cases: '!max-h-[288px] overflow-y-auto',
		Subject: '!max-h-[528px] overflow-y-auto',
		Date: '!max-h-[528px] overflow-y-auto',
	};
	return (
		<table
			className={clsx(
				'bg-gray-20 rounded-tl-2xl rounded-tr-2xl py-2 px-4 w-full h-10 hidden md:block',
				className
			)}
		>
			<tbody className="flex items-center w-full h-full">
				<tr
					className="grid w-full sm:grid-rows-[auto]
					sm:grid-cols-[370px_1fr_1fr_100px_60px] 3xl:grid-cols-[470px_1fr_1fr_100px_60px]"
				>
					{Object.entries(filters).map(([key, value], index) => (
						<td key={index}>
							<div
								className={clsx('flex items-center gap-2', {
									'place-self-end flex justify-end':
										headersAlignEnd?.includes(index),
								})}
							>
								<Typography type="regular-12" className="opacity-60">
									{titleMap[key] || key}
								</Typography>
								{!Array.isArray(value) && key === 'Date' && value}
								{Array.isArray(value) && value.length > 0 && (
									<Combobox
										clearAll={() => handleSelectFilters(key, '')}
										title={titleMap[key] || key}
										buttonContent={
											<div className="relative w-fit">
												<Icon src={Icons.filter} className="!w-4 !h-4" />
												<div
													className={clsx(
														'bg-[#2084E7] rounded-full w-[10px] h-[10px] absolute -right-2 -top-[0.1rem] border border-white',
														{
															hidden: !params.get(key.toLowerCase()),
														}
													)}
												/>
											</div>
										}
										open={indexOpen.includes(index)}
										classNamePopoverContent={clsx(widthMap[key])}
										placeholder=""
										options={value}
										value={''}
										setOpen={() => handleOpen(index)}
										setValue={(data) => handleSelectFilters(key, data)}
										classNameCommandList={commandListClassNameMap[key]}
									/>
								)}
							</div>
						</td>
					))}
					<td className="px-6 max-w-20"></td>
				</tr>
			</tbody>
		</table>
	);
};
